import { createContext, useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import HumburgerMenu from "../pages/layout/sidebar";
import { sidebarDrawerInit, getCookies } from "../helper/utils";
import { openRoutes } from "./route-list";
import NotFound from "../pages/not-found";

import Chat from "../pages/chat";
import FloatingLoader from "../pages/layout/floatLoader";
import Footer from "../pages/layout/footer";

export const ThemeContext = createContext();
export const GameContext = createContext();
export const useTheme = () => useContext(ThemeContext);

const PrivateRoute = ({ path, element, isAuthProtected }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname, search, hash } = location;

  const auth = getCookies("betfrenzy_token");
  const [menuOpen, setMenuOpen] = useState(!sidebarDrawerInit);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { data: tournamentsData } = useSelector(
    (state) => state.tournamentsList
  );
  const { data: profileData } = useSelector((state) => state.getProfile);

  const width = window.innerWidth;

  /** -------------------------------
   * 🔒 Redirect unauthenticated users
   ---------------------------------*/
  useEffect(() => {
    if (isAuthProtected && !auth) {
      // setIsRedirecting(true);
      // toast.error("Authentication required");
      setTimeout(() => {
        // navigate("/", { state: { fromLogin: true } });
        // setIsRedirecting(false);
      }, 500);
    }
  }, [isAuthProtected, auth, navigate]);

  /** -------------------------------
   * 🌀 Scroll to top on route change
   ---------------------------------*/
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 50);
    return () => clearTimeout(timer);
  }, [pathname, search, hash]);

  /** -------------------------------
   * 🎨 Theme management
   ---------------------------------*/
  const [theme, setTheme] = useState("Dark");

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved) {
      setTheme(saved);
      document.documentElement.setAttribute("data-theme", saved);
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    if (theme == "Dark") {
      root.classList.add("dark-theme");
      root.classList.remove("light-theme");
    } else {
      root.classList.add("light-theme");
      root.classList.remove("dark-theme");
    }
  }, [theme]);

  const toggleTheme = (newTheme) => {
    setTheme(newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
    localStorage.setItem("theme", newTheme);
  };

  /** -------------------------------
   * 🛡 VPN Token Handler
   ---------------------------------*/
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const vpnToken = params.get("vpnToken");
    if (vpnToken) {
      localStorage.setItem("vpnToken", vpnToken);
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [location.search]);

  /** -------------------------------
   * ⛔ Prevent scroll when modal is active
   ---------------------------------*/
  useEffect(() => {
    const updateOverflow = () => {
      document.body.style.overflow = document.querySelector(".show-animate")
        ? "hidden"
        : "auto";
    };

    const observer = new MutationObserver(updateOverflow);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    updateOverflow();
    return () => observer.disconnect();
  }, []);

  /** -------------------------------
   * 🖼 Tournament background
   ---------------------------------*/
  const routeId = pathname.match(/^\/tournaments\/view\/([^/]+)$/)?.[1] || null;

  const tournament = tournamentsData?.data?.find((t) => t.id == routeId);
  const isMobile = window.innerWidth <= 992;

  const backgroundImage =
    width > 1024
      ? tournament?.background_image
      : tournament?.background_image_for_mobile;

  const backgroundStyle = backgroundImage
    ? {
        backgroundImage: `linear-gradient(rgb(0 0 0 / 9%), rgb(0 0 0 / 17%)), url(${backgroundImage})`,
        backgroundSize: "100% auto",
        backgroundRepeat: "no-repeat",
        backgroundPositionX: "0",
        marginTop: width > 1024 ? "0px" : "15%",
      }
    : {
        //
        minHeight: "100vh",
        // height: "100%",
      };

  /** -------------------------------
   * 📐 Layout paddings
   ---------------------------------*/

  const isAffiliate = pathname == "/affiliate-section";
  const pageStyles =
    width > 1024
      ? !openRoutes.some((r) => r.path == pathname) && {
          // path
          // marginLeft: menuOpen ? width * 0.17 : "5px",
          // marginRight: "2%",
          paddingTop: profileData?.user?.isDemoUser
            ? "80px"
            : isAffiliate
            ? "88px"
            : "68px",
          paddingBottom: "80px",
          paddingLeft: isAffiliate ? "0px" : width < 768 ? "8px" : "10px",
          paddingRight: isAffiliate ? "0px" : width < 768 ? "8px" : "10px",
          transition: isAffiliate ? "none" : "all 0.3s ease",
        }
      : !openRoutes.some((r) => r.path == pathname) && {
          // path
          marginLeft: width < 768 ? "8px" : "10px",
          marginRight: width > 768 ? "" : width < 768 ? "8px" : "10px",
          paddingTop: width < 768 ? "80px" : "105px",
          paddingBottom:
            location.state?.game_id == "550000" || pathname == "/game-launch"
              ? "0px"
              : width < 768
              ? "85px"
              : "10px",
          // width < 1024
          //   ? `${navbarHeight + 50}px` // + 50
          //   : "10px",
        };

  /** -------------------------------
   * ⏳ Loader while redirecting
   ---------------------------------*/
  if (isRedirecting) return <FloatingLoader />;

  /** -------------------------------
   * 🚀 Render
   ---------------------------------*/
  return (
    <div className="h-100">
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        {/* <TawkProvider> */}
        {/* <HumburgerMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} /> */}

        {!openRoutes.some((r) => r.path == path) && (
          <>
            {!isAffiliate && (
              <HumburgerMenu menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
            )}
            <Chat />
          </>
        )}

        <div
          id="page-wrap"
          className="page-wrap"
          style={
            window.innerWidth > 1024
              ? backgroundStyle
              : {
                  ...backgroundStyle,
                  // overflowY: "scroll",
                  scrollbarWidth: "none",
                  paddingBottom: "-10px !important",
                }
          }
        >
          {path == "*" && <NotFound />}
          <div
            className="h-100"
            style={{
              ...pageStyles,
              paddingBottom:
                location?.state?.game_id == "550000" || pathname == "/game-launch"
                  ? "0px"
                  : "2px",
            }}
          >
            <div className="flex justify-center">
              <div
                className={`${
                  !isMobile && !isAffiliate
                    ? "middle-container opacity-based-on-search"
                    : ""
                }`}
              >
                {element}
              </div>
            </div>

            {location.state?.game_id !== "550000" &&
              location.pathname !== "/affiliate-section" &&
              location.pathname !== "/game-launch" &&
              !/^\/tournaments\/view\/[^/]+$/.test(location.pathname) && (
                <Footer />
              )}
          </div>
        </div>

        {/* </TawkProvider> */}
      </ThemeContext.Provider>
    </div>
  );
};

export default PrivateRoute;
